import { FormProvider } from 'react-hook-form';

import type { FormProps } from './types';
import type { SyntheticEvent } from 'react';
import type { FieldValues } from 'react-hook-form';

import {
  FormAsyncAutoComplete,
  FormCheckbox,
  FormDatePicker,
  FormImageUpload,
  FormInput,
  FormMultiSelect,
  FormPhoneInput,
  FormSelect,
  FormTextarea,
  FormTextEditor,
} from '@/components/FormFields';
import { FieldGroup } from '@/components/ui/field';

/**
 * Main Form component
 * Handles form logic (FormProvider, form element, FieldGroup)
 * Should be wrapped with FormWrapper for UI (Card, header, buttons)
 * Exports all field components as static properties (Form.Input, Form.Textarea, etc.)
 */
const Form = <TFieldValues extends FieldValues = FieldValues>(
  props: FormProps<TFieldValues>,
) => {
  const { form, id, onSubmit, children, className, fieldGroupClassName } =
    props;

  const onFormSubmit = onSubmit
    ? (event: SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit();
      }
    : undefined;

  return (
    <FormProvider {...form}>
      <form
        noValidate
        id={id}
        onSubmit={onFormSubmit}
        className={className}
      >
        <FieldGroup className={fieldGroupClassName}>{children}</FieldGroup>
      </form>
    </FormProvider>
  );
};

// Export field components as static properties
Form.Input = FormInput;
Form.Textarea = FormTextarea;
Form.DatePicker = FormDatePicker;
Form.ImageUpload = FormImageUpload;
Form.Checkbox = FormCheckbox;
Form.Select = FormSelect;
Form.MultiSelect = FormMultiSelect;
Form.TextEditor = FormTextEditor;
Form.AutoComplete = FormAsyncAutoComplete;
Form.PhoneInput = FormPhoneInput;

export { Form };
export type { FormProps } from './types';
